import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/apiConfig';
import fetchApi from '../utils/api';
import { toast } from 'react-toastify';
import axios from 'axios';

import '../styles/UserForm.css';
import Swal from 'sweetalert2';

function PlanForm({ plan, onCancel, onSuccess }) {
    const [formData, setFormData] = useState({
        planName: '',
        planDurationDays: '',
        planPrice: '',
        planDiscount: '',
        planDescription: '',
        planModules: []
    });
    
    const [availableModules, setAvailableModules] = useState([]);

    useEffect(() => {
        const fetchModules = async () => {
            try {
                const token = sessionStorage.getItem('token');
                const response = await axios.get(`${API_BASE_URL}/user-permissions/metadata`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data && response.data.modules) {
                    setAvailableModules(response.data.modules.filter(m => m !== 'Dashboard' && m !== 'Plan Payment'));
                }
            } catch(e) {
                console.error("Failed to fetch modules", e);
            }
        };
        fetchModules();
    }, []);

    useEffect(() => {
        if (plan) {
            setFormData({
                planName: plan.planName || '',
                planDurationDays: plan.planDurationDays || '',
                planPrice: plan.planPrice || '',
                planDiscount: plan.planDiscount || '',
                planDescription: plan.planDescription || '',
                planModules: plan.planModules || []
            });
        }
    }, [plan]);

    const handleModuleToggle = (moduleName) => {
        setFormData(prev => {
            const currentModules = prev.planModules || [];
            if (currentModules.includes(moduleName)) {
                return { ...prev, planModules: currentModules.filter(m => m !== moduleName) };
            } else {
                return { ...prev, planModules: [...currentModules, moduleName] };
            }
        });
    };

    const handleSelectAllModules = () => {
        if ((formData.planModules || []).length === availableModules.length && availableModules.length > 0) {
            setFormData(prev => ({ ...prev, planModules: [] }));
        } else {
            setFormData(prev => ({ ...prev, planModules: [...availableModules] }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        const conf = await Swal.fire({
            title: 'Save Data?',
            text: 'Are you sure you want to save this data?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#00334e',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'No, Keep Editing'
        });
        if (!conf.isConfirmed) return;
        try {
            const url = plan ? `/plan/${plan._id}` : '/plan';
            const method = plan ? 'PUT' : 'POST';

            await fetchApi(url, {
                method,
                body: JSON.stringify(formData)
            });

            toast.success(`Plan ${plan ? 'updated' : 'created'} successfully!`);
            onSuccess();
        } catch (error) {
            console.error('Error saving plan:', error);
            toast.error(error.message || 'Failed to save plan');
        }
    };

    const requiredStar = <span style={{ color: '#ef4444' }}>*</span>;

    return (
        <div className="list-page-container">
            <div className="modal-header" style={{ marginBottom: '24px' }}>
                <h2 className="modal-title" style={{ fontSize: '24px', fontWeight: '700', color: '#0c3447' }}>
                    {plan ? 'Update Plan' : 'Add New Plan'}
                </h2>
            </div>

            <div className="user-form-body">
                <form id="plan-form" onSubmit={handleSubmit}>
                    <div className="user-grid">
                        <div className="user-field">
                            <label>Plan Name {requiredStar}</label>
                            <input
                                type="text"
                                name="planName"
                                value={formData.planName}
                                onChange={handleChange}
                                required
                                placeholder="Enter Plan Name"
                            />
                        </div>

                        <div className="user-field">
                            <label>Duration (Days) {requiredStar}</label>
                            <input
                                type="number"
                                name="planDurationDays"
                                value={formData.planDurationDays}
                                onChange={handleChange}
                                required
                                min="1"
                                placeholder="Enter Duration in Days"
                            />
                        </div>

                        <div className="user-field">
                            <label>Plan Price (₹)</label>
                            <input
                                type="number"
                                name="planPrice"
                                value={formData.planPrice}
                                onChange={handleChange}
                                placeholder="Enter Basic Price"
                                min="0"
                            />
                        </div>

                        <div className="user-field">
                            <label>Discount (%)</label>
                            <input
                                type="number"
                                name="planDiscount"
                                value={formData.planDiscount}
                                onChange={handleChange}
                                placeholder="Enter Discount %"
                                min="0"
                                max="100"
                            />
                        </div>

                        <div className="user-field">
                            <label>Final Price (₹)</label>
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#16a34a',
                                border: '1px solid #d1d5db'
                            }}>
                                ₹{(() => {
                                    const price = Number(formData.planPrice) || 0;
                                    const discount = Number(formData.planDiscount) || 0;
                                    return (price - (price * (discount / 100))).toFixed(2);
                                })()}
                            </div>
                        </div>

                        <div className="user-field full-width">
                            <label>Description</label>
                            <textarea
                                name="planDescription"
                                value={formData.planDescription}
                                onChange={handleChange}
                                rows="4"
                                placeholder="Describe plan features..."
                                style={{
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    outline: 'none',
                                    width: '100%',
                                    resize: 'vertical'
                                }}
                            ></textarea>
                        </div>
                    </div>

                    <div className="user-field full-width" style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
                            <label style={{ fontSize: '18px', color: '#0c3447', margin: 0 }}>
                                Included Modules / Features {requiredStar}
                            </label>
                            <button
                                type="button"
                                onClick={handleSelectAllModules}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#0c3447',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                {(formData.planModules || []).length === availableModules.length && availableModules.length > 0 ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                            {availableModules.map(moduleName => (
                                <label key={moduleName} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <input
                                        type="checkbox"
                                        checked={(formData.planModules || []).includes(moduleName)}
                                        onChange={() => handleModuleToggle(moduleName)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontSize: '14px', color: '#334155', fontWeight: '500' }}>{moduleName}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="user-form-actions" style={{ marginTop: '30px' }}>
                        <button type="submit" className="btn-submit">
                            {plan ? 'Update Plan' : 'Save Plan'}
                        </button>
                        <button type="button" className="btn-cancel" onClick={onCancel}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default PlanForm;
